import React from 'react';
import { Button, ButtonVariant, CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';
import { Box, Typography } from '@mui/material';
import { LoanStatus } from '../../HbtIndividualLoanDetails/types';
import { LoanInventoryTableFields } from '../../HbtPortfolioLoansInventoryTable/types';
import { FlyoutLoanItem } from './flyoutLoanItem';
import styles from './style.module.scss';

interface LoansDropDownProps {
  buttonValue: string;
  buttonAriaText: string;
  showLoans: boolean;
  setShowLoans: (value: React.SetStateAction<boolean>) => void;
  loans: LoanInventoryTableFields[];
  removeLoanFn: (item: LoanInventoryTableFields) => void;
  showSelectedLoansError?: boolean;
  errMsg?: string;
  iconType?: string;
  loanStatus?: LoanStatus | undefined;
}

/**
 * @param {string} props.buttonValue - the text on the button.
 * @param {string} props.buttonAriaText - the aria text on the button.
 * @param {booleans} props.showLoans - show or hide the loans drop down.
 * @param {function} props.setShowLoans - click event handler to show or hide loans.
 * @param {LoanInventoryTableFields[]} props.loans - the list of loans in the drop down.
 * @param {function} props.removeLoanFn - click to remove loan from the list of loans.
 * @param {string} props.showSelectedLoansError - show the error in the loan drop down.
 * @param {string} props.errMsg - the error msg to be displayed.
 * @param {string} props.iconType - the type of icon to be shown in the error msg container.
 * @param {LoanStatus} props.loanStatus - the  status of the loan.
 * @returns {JSX.Element} Rendered the icon component.
 */

export const LoansDropDown = ({
  buttonValue,
  buttonAriaText,
  showLoans,
  setShowLoans,
  loans,
  removeLoanFn,
  showSelectedLoansError,
  errMsg,
  iconType,
  loanStatus
}: LoansDropDownProps) => {
  return (
    <Box className={styles.loansDropDown}>
      <Button
        type="button"
        name={`btn`}
        onClick={() => setShowLoans(!showLoans)}
        variant={ButtonVariant.TERTIARY}
        text={{
          value: buttonValue
        }}
        ariaText={{
          value: buttonAriaText
        }}
      />
      {showLoans && (
        <Box className={styles.selectedLoansContainer}>
          {loans.map((loan, index) => {
            const isError = loanStatus ? loanStatus === loan.loanStatus : false;
            return (
              <FlyoutLoanItem
                key={index}
                loanNumber={loan.lenderReferenceNumber}
                loanStatus={loan.loanStatus}
                error={isError}
                loan={loan}
                removeItem={removeLoanFn}
                showIconButton={loans.length !== 1}
                hoverEnabled={loans.length !== 1}
              />
            );
          })}
        </Box>
      )}
      {showSelectedLoansError && (
        <Box className={styles.loansErrorDesc}>
          <CmhcIcon size={CmhcIconSize.SMALL} type={iconType} />
          <Typography className={styles.loansError}>{errMsg}</Typography>
        </Box>
      )}
    </Box>
  );
};
